import React, { useState, useEffect } from "react";
import { useTable, useSortBy } from "react-table";
import { TextFilter, SelectFilter, DateFilter } from "./Filter";
import Pagination from "../pagination/Pagination";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { HashLoader } from "react-spinners";

const customStyles = {
  wrapper: {
    display: "flex",
    height: "100vh",
    width: "100%",
  },
  rows: {
    style: {
      fontSize: "16px",
    },
  },
  headCells: {
    style: {
      fontSize: 16,
      color: "#747081",
      fontFamily: "Prompt, sans-serif",
    },
  },
  cells: {
    style: {
      fontSize: 16,
      color: "#747081",
      fontFamily: "Prompt, sans-serif",
    },
  },
  blocktable: {
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#d6d4d9",
    borderRadius: "0.5rem",
    marginBottom: "4rem",
    width: "100%",
  },
  button: {
    height: "40px",
    background: "#70B9F0 0% 0% no-repeat padding-box",
    borderRadius: "4px",
    padding: "8px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
  },
};

const TableSearchV1 = ({
  columns,
  data,
  filters,
  setFilters,
  loading,
  total,
  page,
  maxPage,
  noDataComponent,
  setPage,
  addData,
  sortBy,
  onSortChange,
}) => {
  const [tableData, setTableData] = useState(data);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: sortBy && sortBy.id ? [{ id: sortBy.id, desc: sortBy.desc }] : [],
      },
    },
    useSortBy,
  );

  useEffect(() => {
    if (sortBy && sortBy.id) {
      setSortBy([{ id: sortBy.id, desc: sortBy.desc }]);
    } else {
      setSortBy([]);
    }
  }, [sortBy, setSortBy]);

  const navigate = useNavigate();

  const handleChangeFilter = (id, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [id]: value,
    }));
  };

  const handleDateChangeFilter = (id, value) => {
    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [id]: value,
      };
      return newFilters;
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= maxPage) {
      setPage(newPage);
    }
  };

  const handleSortChange = (column) => {
    const currentDesc = sortBy && sortBy.id === column.id ? sortBy.desc : false;
    const desc = !currentDesc;
    if (onSortChange) {
      onSortChange(column.id, desc);
    } else {
      setSortBy([{ id: column.id, desc }]);
    }
  };

  const firstPageRows = rows.slice(0, 10);

  return (
    <div className="w-full bg-white rounded-[9px] border-[1px] border-[#E0DFE2]">
      {addData ? (
        <div className="flex md:w-full items-end md:py-1 gap-4 py-6 w-full pl-[15px]">
          <div
            style={{
              ...customStyles.button,
              cursor: addData.url || addData.state ? "pointer" : "",
            }}
            onClick={() => {
              if (addData.url) {
                const navigationState = addData.type
                  ? { type: addData.type }
                  : {};
                navigate(addData.url, { state: navigationState });
              }

              if (addData.state) {
                addData.state();
              }
            }}
          >
            <FaPlus className="mr-1" /> {addData.name}
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="overflow-x-auto border-y-[1px] border-[#E0DFE2]">
        <table {...getTableProps()} className="min-w-full border-none">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="p-[0px_0px] border-none"
                  >
                    <div className="flex flex-wrap h-max">
                      <div
                        className={`py-3 px-2 w-full text-left border-b-[#C8C6CD] border-b-[1px] text-[#747081] text-[16px] flex flex-row justify-center items-center whitespace-nowrap ${column.disableSortBy ? '' : 'cursor-pointer'}`}
                        onClick={() => { if (!column.disableSortBy) handleSortChange(column); }}
                      >
                        {column.render("Header")}
                        {!column.disableSortBy && (
                          column.isSortedDesc === true ? (
                            <FaSortDown color="#747081" />
                          ) : column.isSortedDesc === false ? (
                            <FaSortUp color="#747081" />
                          ) : column.isSortedDesc === undefined ? (
                            <FaSort color="#eeeeee" />
                          ) : (
                            <FaSort color="#747081" />
                          )
                        )}
                      </div>
                      <div className="p-1 w-full h-[50px] border-b-[#C8C6CD] border-b-[1px] flex justify-center items-center">
                        {column.Filter === "text" && (
                          <TextFilter
                            columnId={column.id}
                            value={filters[column.id] || ""}
                            onChange={handleChangeFilter}
                          />
                        )}
                        {column.Filter === "select" && (
                          <SelectFilter
                            columnId={column.id}
                            value={filters[column.id] || ""}
                            options={column.DataFilter}
                            onChange={handleChangeFilter}
                            placeholder={
                              column?.status === true ? "เลือกสถานะ" : "เลือก"
                            }
                          />
                        )}
                        {column.Filter === "date" && (
                          <DateFilter
                            columnId={column.id}
                            value={filters[column.id] || ""}
                            onChange={handleDateChangeFilter}
                          />
                        )}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <></>
          ) : (
            <tbody {...getTableBodyProps()}>
              {firstPageRows.length > 0 &&
                firstPageRows.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      style={{ borderBottom: "1px solid #eeeeee" }}
                      className="text-[#747081] text-[16px]"
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="border-0 bg-white text-left text-[#747081] px-3 py-2 whitespace-nowrap"
                        >
                          {cell.column.id === "id"
                            ? (page - 1) * 10 + i + 1
                            : cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
            </tbody>
          )}
        </table>
        {loading ? (
          <div className="flex flex-col justify-center items-center h-40 w-full">
            <HashLoader color="#FF9768" size={70} />
            <div
              style={{
                marginTop: "20px",
                color: "#FF9768",
                fontWeight: "600",
                fontSize: "18px",
              }}
            >
              loading...
            </div>
          </div>
        ) : (
          rows.length === 0 && (
            <div className="flex justify-center items-center p-[9px_6px]">
              <div>{noDataComponent}</div>
            </div>
          )
        )}
      </div>

      <div className="flex items-center">
        <div className="md:text-[12px] text-[#d6d4da] mt-2 mb-2 pl-4 text-left">
          {`Showing ${total === 0 ? 0 : (page - 1) * 10 + 1} to ${Math.min(
            page * 10,
            total,
          )} of ${total} entries`}
        </div>

        <div className="flex-1 flex justify-end">
          <Pagination
            page={page}
            totalpage={maxPage}
            clickForward={() => handlePageChange(page + 1)}
            clickBackward={() => handlePageChange(page - 1)}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default TableSearchV1;
